import React, { useEffect, useId } from "react";
import { Link } from "react-router-dom";
import { ArrowDownRight, ArrowUpRight, ChevronRight, Minus } from "lucide-react";

export function cx(...parts) {
  return parts.filter(Boolean).join(" ");
}

export function BrandMark({ className = "brand__mark" }) {
  const id = useId().replace(/:/g, "");
  return (
    <svg className={className} viewBox="0 0 34 34" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={`g${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#4a64ff" />
          <stop offset="1" stopColor="#14b8a6" />
        </linearGradient>
      </defs>
      <rect width="34" height="34" rx="10" fill={`url(#g${id})`} />
      <path d="M8 25.5h18" stroke="rgba(255,255,255,.4)" strokeWidth="1.5" strokeLinecap="round" />
      <path
        d="M8 9.5c5.5 0 8.5 3 11 7.5s4.5 7.6 7 7.6"
        fill="none"
        stroke="#fff"
        strokeWidth="2.3"
        strokeLinecap="round"
      />
      <circle cx="26" cy="24.6" r="2.5" fill="#fff" />
    </svg>
  );
}

export function Card({
  title,
  subtitle,
  actions,
  children,
  footer,
  variant,
  className,
  bodyClassName,
  as: Tag = "section",
  ...rest
}) {
  return (
    <Tag className={cx("card", variant && `card--${variant}`, className)} {...rest}>
      {(title || actions) && (
        <header className="card__header">
          <div>
            {title && <h2 className="card__title">{title}</h2>}
            {subtitle && <p className="card__subtitle">{subtitle}</p>}
          </div>
          {actions && <div className="row no-print">{actions}</div>}
        </header>
      )}
      {children != null && <div className={cx("card__body", bodyClassName)}>{children}</div>}
      {footer && <div className="card__footer">{footer}</div>}
    </Tag>
  );
}

export function StatTile({ label, value, icon: Icon, meta, badge, hero, variant, title }) {
  return (
    <div className={cx("card", variant && `card--${variant}`)}>
      <div className="stat">
        <div className="stat__top">
          <span className="stat__label">
            {Icon && (
              <span className="stat__icon" aria-hidden="true">
                <Icon />
              </span>
            )}
            {label}
          </span>
          {badge}
        </div>
        <div className={cx("stat__value", hero && "stat__value--hero")} title={title}>
          {value}
        </div>
        {meta && <div className="stat__meta">{meta}</div>}
      </div>
    </div>
  );
}

/**
 * Signed change with direction arrow. `good` says which direction is good
 * ("down" for debt, "up" for savings) so colour follows meaning, not sign.
 */
export function Delta({ direction, good = "up", children }) {
  const Icon = direction === "up" ? ArrowUpRight : direction === "down" ? ArrowDownRight : Minus;
  const tone = direction === "flat" || !direction ? "neutral" : direction === good ? "good" : "bad";
  return (
    <span className={`delta delta--${tone}`}>
      <Icon aria-hidden="true" />
      {children}
    </span>
  );
}

export function Badge({ tone, icon: Icon, children, className }) {
  return (
    <span className={cx("badge", tone && `badge--${tone}`, className)}>
      {Icon && <Icon aria-hidden="true" />}
      {children}
    </span>
  );
}

export function Meter({ value, tone, label }) {
  const pct = Math.max(0, Math.min(1, value || 0)) * 100;
  return (
    <div
      className={cx("meter", tone && `meter--${tone}`)}
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      aria-label={label}
    >
      <div className="meter__fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Notice({ icon: Icon, title, children, tone, actions, className }) {
  return (
    <div className={cx("notice", tone && `notice--${tone}`, className)} role="note">
      {Icon && <Icon aria-hidden="true" />}
      <div className="notice__body">
        {title && <div className="notice__title">{title}</div>}
        {children && <div>{children}</div>}
        {actions && <div className="notice__actions">{actions}</div>}
      </div>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, children, actions }) {
  return (
    <div className="empty">
      {Icon && (
        <div className="empty__icon" aria-hidden="true">
          <Icon />
        </div>
      )}
      <h2 className="empty__title">{title}</h2>
      {children && <p className="empty__desc">{children}</p>}
      {actions && <div className="empty__actions">{actions}</div>}
    </div>
  );
}

export function Segmented({ options, value, onChange, label, size }) {
  return (
    <div className={cx("segmented", size && `segmented--${size}`)} role="group" aria-label={label}>
      {options.map((o) => {
        const Icon = o.icon;
        return (
          <button
            key={String(o.value)}
            type="button"
            className="segmented__btn"
            aria-pressed={o.value === value}
            onClick={() => onChange(o.value)}
            title={o.title}
          >
            {Icon && <Icon aria-hidden="true" />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Switch({ checked, onChange, children, id }) {
  return (
    <label className="switch" htmlFor={id}>
      <input
        id={id}
        type="checkbox"
        role="switch"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="switch__track" aria-hidden="true" />
      <span>{children}</span>
    </label>
  );
}

export function Chips({ options, value, onSelect, label }) {
  return (
    <div className="chips" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          className="chip"
          aria-pressed={o.value === value}
          onClick={() => onSelect(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Skeleton({ height = 16, width = "100%", style, className }) {
  return (
    <div
      className={cx("skeleton", className)}
      style={{ height, width, ...style }}
      aria-hidden="true"
    />
  );
}

export function PageHeader({ title, description, breadcrumbs, actions, badge }) {
  useEffect(() => {
    if (typeof title === "string") document.title = `${title} · Loan Recovery Pro`;
  }, [title]);
  return (
    <header className="page-header">
      <div className="page-header__text">
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav aria-label="Breadcrumb">
            <ol className="breadcrumb">
              {breadcrumbs.map((b, i) => (
                <li key={b.label} className="row" style={{ gap: 6 }}>
                  {i > 0 && <ChevronRight aria-hidden="true" />}
                  {b.to ? <Link to={b.to}>{b.label}</Link> : <span aria-current="page">{b.label}</span>}
                </li>
              ))}
            </ol>
          </nav>
        )}
        <div className="row row--wrap" style={{ gap: 10 }}>
          <h1 className="page-header__title">{title}</h1>
          {badge}
        </div>
        {description && <p className="page-header__desc">{description}</p>}
      </div>
      {actions && <div className="page-header__actions">{actions}</div>}
    </header>
  );
}

export function SeriesSwatch({ color, kind = "rect" }) {
  return (
    <span
      className={cx("swatch", kind === "line" && "swatch--line", kind === "dot" && "swatch--dot")}
      style={{ background: color }}
      aria-hidden="true"
    />
  );
}
